import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { nbaToday, shortDate } from "@/lib/format";
import "./PrintValue.css";

const COMPS = [{ value: "", label: "NBA" }, { value: "E", label: "EuroLeague" }, { value: "U", label: "EuroCup" }];
const PHASES = [{ value: "both", label: "Pregame + Live" }, { value: "pregame", label: "Μόνο Pregame" }, { value: "live", label: "Μόνο Live/Ημίχρονο" }];
const resLabel = (r) => r === "win" ? "Κέρδος" : r === "loss" ? "Ήττα" : r === "push" ? "Push" : r === "void" ? "Άκυρο" : "Εκκρεμεί";
const resCls = (r) => r === "win" ? "pv-win" : r === "loss" ? "pv-loss" : r === "push" ? "pv-push" : "pv-pend";
const money = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v}u`);
const evTxt = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${(v * 100).toFixed(1)}%`);

function PickTable({ rows }) {
  return (
    <table className="pv-table" data-testid="pv-table">
      <thead>
        <tr><th>Τύπος</th><th>Επιλογή</th><th>Market</th><th>Odds</th><th>EV</th><th>Αποτέλεσμα</th><th>Κέρδος</th></tr>
      </thead>
      <tbody>
        {rows.map((p) => (
          <tr key={p.id} data-testid={`pv-row-${p.id}`}>
            <td><span className={`pv-kind pv-kind-${p.kind}`}>{p.kind === "team" ? "ΟΜ" : "ΠΑ"} · {p.phase === "live" ? "LIVE" : "PRE"}</span></td>
            <td className="pv-title"><b>{p.title}</b>{p.label && p.label !== p.title ? <span> {p.label}</span> : null}</td>
            <td>{p.market_label}</td>
            <td>{p.odds != null ? Number(p.odds).toFixed(2) : "—"}</td>
            <td>{p.ev == null ? (p.edge_label || "—") : evTxt(p.ev)}</td>
            <td className={resCls(p.result)}>{resLabel(p.result)}</td>
            <td className={p.result && p.result !== "void" ? resCls(p.result) : ""}>{p.result && p.result !== "void" ? money(p.profit) : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function PrintValue() {
  const [sp] = useSearchParams();
  const today = nbaToday();
  const [comp, setComp] = useState(sp.get("comp") ?? "");
  const [phase, setPhase] = useState(sp.get("phase") || "both");
  const [start, setStart] = useState(sp.get("start") || today);
  const [end, setEnd] = useState(sp.get("end") || sp.get("start") || today);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true); setErr(null);
    api.get("/value/print", { params: { comp: comp || undefined, phase, start, end } })
      .then((r) => setData(r.data)).catch((e) => setErr(errorText(e))).finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const byMarket = useMemo(() => {
    const m = {};
    (data?.picks || []).forEach((p) => { (m[p.market_label || "—"] = m[p.market_label || "—"] || []).push(p); });
    return Object.entries(m).sort((a, b) => b[1].length - a[1].length);
  }, [data]);
  const byGame = useMemo(() => {
    const g = {};
    (data?.picks || []).forEach((p) => { const k = p.game_id || "—"; (g[k] = g[k] || []).push(p); });
    return Object.entries(g).sort((a, b) => (a[1][0]?.day || "").localeCompare(b[1][0]?.day || ""));
  }, [data]);

  const s = data?.summary;
  const rangeLabel = start === end ? shortDate(start) : `${shortDate(start)} — ${shortDate(end)}`;
  const compLabel = COMPS.find((c) => c.value === comp)?.label || "NBA";

  return (
    <div className="pv-root" data-testid="print-value">
      <div className="pv-controls" data-testid="pv-controls">
        <Link to="/halftime-value" className="pv-back" data-testid="pv-back"><ArrowLeft size={15} /> Πίσω</Link>
        <label>Διοργάνωση
          <select value={comp} onChange={(e) => setComp(e.target.value)} data-testid="pv-comp">
            {COMPS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>
        <label>Τύπος
          <select value={phase} onChange={(e) => setPhase(e.target.value)} data-testid="pv-phase">
            {PHASES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
        <label>Από<input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="pv-start" /></label>
        <label>Έως<input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="pv-end" /></label>
        <button type="button" className="pv-btn" onClick={load} disabled={busy} data-testid="pv-apply">
          {busy ? <Loader2 size={15} className="pv-spin" /> : "Ανανέωση"}
        </button>
        <button type="button" className="pv-btn pv-print" onClick={() => window.print()} data-testid="pv-print"><Printer size={15} /> Εκτύπωση</button>
      </div>

      <div className="pv-sheet">
        <header className="pv-head">
          <h1>Value Bets · {compLabel}</h1>
          <p>{rangeLabel} · {PHASES.find((p) => p.value === phase)?.label}</p>
        </header>

        {err && <p className="pv-empty" data-testid="pv-error">{err}</p>}
        {busy && !data && <p className="pv-empty">Φόρτωση…</p>}

        {s && (
          <div className="pv-summary" data-testid="pv-summary">
            <div><b>{s.total}</b><span>Picks</span></div>
            <div><b>{s.wins}-{s.losses}{s.pushes ? `-${s.pushes}` : ""}</b><span>Β-Η{s.pushes ? "-Π" : ""}</span></div>
            <div className={s.profit > 0 ? "pv-win" : s.profit < 0 ? "pv-loss" : ""}><b>{money(s.profit)}</b><span>Κέρδος</span></div>
            <div><b>{s.roi == null ? "—" : `${(s.roi * 100).toFixed(1)}%`}</b><span>ROI</span></div>
            <div><b>{s.pending}</b><span>Εκκρεμή</span></div>
          </div>
        )}

        {data && !data.picks.length && <p className="pv-empty" data-testid="pv-empty">Δεν υπάρχουν value bets για αυτή την επιλογή.</p>}

        {data && data.picks.length > 0 && (
          <>
            <section className="pv-section" data-testid="pv-by-market">
              <h2>Ανά Market</h2>
              {byMarket.map(([label, rows]) => (
                <div key={label} className="pv-group">
                  <h3>{label} <span>({rows.length})</span></h3>
                  <PickTable rows={rows} />
                </div>
              ))}
            </section>

            <section className="pv-section" data-testid="pv-by-game">
              <h2>Ανά Αγώνα</h2>
              {byGame.map(([gid, rows]) => (
                <div key={gid} className="pv-group">
                  <h3>{data.games[gid] || gid} <span>· {shortDate(rows[0]?.day)} ({rows.length})</span></h3>
                  <PickTable rows={rows} />
                </div>
              ))}
            </section>
          </>
        )}
        <footer className="pv-foot">Δημιουργήθηκε {new Date().toLocaleString("el-GR")}</footer>
      </div>
    </div>
  );
}
